import { localStorageService, StorageKeys } from '@ibk/shared-utils';
import { config } from '../config';
import { selectIsSessionExpiredByInactivity, selectRefreshToken } from '../store/selectors/tokensSelectors';
import { clearTokens, resetSessionExpiredByInactivity, setTokens } from '../store/slices/tokensSlice';
const ERROR_CODES = {
    PIN_FAILURE_TO_VALIDATE: 'PIN_FAILURE_TO_VALIDATE',
    LOGIN_MAINTENANCE: 'LOGIN_MAINTENANCE',
};
export const SESSION_MAINTENANCE_EVENT = 'session-maintenance';
let refreshPromise = null;
let lastRefreshTime = 0;
const REFRESH_DEBOUNCE_MS = 5000;
export const attemptTokenRefresh = async (api) => {
    const now = Date.now();
    if (refreshPromise) {
        return refreshPromise;
    }
    if (now - lastRefreshTime < REFRESH_DEBOUNCE_MS) {
        return false;
    }
    refreshPromise = performTokenRefresh(api);
    lastRefreshTime = now;
    try {
        const result = await refreshPromise;
        return result;
    }
    finally {
        refreshPromise = null;
    }
};
const dispatchForceSessionExpiration = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('force-session-expiration'));
    }
};
const dispatchSessionMaintenance = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_MAINTENANCE_EVENT));
    }
};
const isMaintenanceResponse = async (response) => {
    if (response.status !== 503)
        return false;
    try {
        const body = await response.json();
        return body?.code === ERROR_CODES.LOGIN_MAINTENANCE;
    }
    catch {
        return false;
    }
};
const performTokenRefresh = async (api) => {
    const state = api.getState();
    const refreshToken = selectRefreshToken(state);
    const isSessionExpiredByInactivity = selectIsSessionExpiredByInactivity(state);
    if (isSessionExpiredByInactivity) {
        return false;
    }
    if (!refreshToken) {
        return false;
    }
    try {
        const refreshResponse = await fetch(`${config.apiBaseUrl}/v1/auth/token`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refreshToken }),
        });
        if (!refreshResponse.ok) {
            if (await isMaintenanceResponse(refreshResponse)) {
                api.dispatch(clearTokens());
                localStorageService.removeItem(StorageKeys.AuthRefreshToken);
                dispatchSessionMaintenance();
                return false;
            }
            dispatchForceSessionExpiration();
            return false;
        }
        const data = await refreshResponse.json();
        if (data.result) {
            api.dispatch(setTokens({
                accessToken: data.result.jwtToken,
                refreshToken: data.result.refreshToken,
                expiresIn: Math.floor((data.result.jwtTokenExpiration - Date.now()) / 1000),
                refreshExpiresIn: Math.floor((data.result.refreshTokenExpiration - Date.now()) / 1000),
                tokenType: 'Bearer',
            }));
            if (typeof window !== 'undefined') {
                localStorageService.setItemSync(StorageKeys.AuthRefreshToken, data.result.refreshToken);
            }
            api.dispatch(resetSessionExpiredByInactivity());
            if (typeof window !== 'undefined') {
                window.dispatchEvent(new CustomEvent('reset-session-timeout'));
            }
            return true;
        }
        dispatchForceSessionExpiration();
        return false;
    }
    catch (error) {
        dispatchForceSessionExpiration();
        return false;
    }
};
export const shouldAttemptRefresh = (error) => {
    if (isPinValidationError(error))
        return false;
    return 'status' in error && (error.status === 401 || error.data === 'AUTH_TOKEN_EXPIRED');
};
const isPinValidationError = (error) => {
    if (!('status' in error) || error.status !== 401)
        return false;
    const errorData = error.data;
    const result = errorData?.result;
    return result?.error === ERROR_CODES.PIN_FAILURE_TO_VALIDATE;
};
export const extractUrl = (args) => {
    return typeof args === 'string' ? args : args.url;
};
export const getTimeout = (args, defaultTimeout = 30000) => {
    return typeof args === 'string' ? defaultTimeout : (args?.timeout ?? defaultTimeout);
};
export const createApiError = (fetchError) => {
    const errorData = fetchError.data;
    return {
        message: errorData?.message || 'An error occurred',
        code: errorData?.code || fetchError.status.toString(),
        status: typeof fetchError.status === 'number' ? fetchError.status : undefined,
        details: errorData,
    };
};
export const createApiErrorSimple = (error) => {
    const errorData = 'data' in error ? error.data : null;
    return {
        message: 'An error occurred',
        details: errorData,
    };
};
export const processTokenFromResponse = (responseData, api) => {
    if (!responseData || typeof responseData !== 'object')
        return;
    const data = responseData;
    if (!data.result || typeof data.result !== 'object')
        return;
    const tokenData = data.result;
    if (!tokenData.jwtToken || typeof tokenData.jwtToken !== 'string')
        return;
    if (!tokenData.refreshToken || typeof tokenData.refreshToken !== 'string')
        return;
    const expiresIn = Math.floor((tokenData.jwtTokenExpiration - Date.now()) / 1000);
    const refreshExpiresIn = tokenData.refreshTokenExpiration
        ? Math.floor((tokenData.refreshTokenExpiration - Date.now()) / 1000)
        : undefined;
    api.dispatch(setTokens({
        accessToken: tokenData.jwtToken,
        refreshToken: tokenData.refreshToken,
        expiresIn,
        refreshExpiresIn,
        tokenType: 'Bearer',
    }));
    if (typeof window !== 'undefined') {
        localStorageService.setItemSync(StorageKeys.AuthRefreshToken, tokenData.refreshToken);
    }
};
export const createAuthHeaders = (state) => {
    const token = state.tokens?.accessToken;
    const tokenType = state.tokens?.tokenType ?? 'Bearer';
    if (token && tokenType) {
        return { Authorization: `${tokenType} ${token}` };
    }
    return {};
};
export const clearRefreshCache = () => {
    refreshPromise = null;
    lastRefreshTime = 0;
};
